/* eslint-disable react-hooks/exhaustive-deps */
import {useCallback, useContext, useEffect, useRef} from 'react'

import Context from '@s-ui/react-context'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {
  DEFAULT_EMPTY_TIME,
  DEFAULT_QUERY_SELECTOR,
  MINIMUM_TIME_TO_BE_VISIBLE,
  OBSERVER_OPTIONS,
  POSITION_DATA_ATTRIBUTE
} from './config.js'

const useViewedAds = ({
  ads = [],
  currentPage,
  extendedTrackingData = {},
  pageName,
  querySelector = DEFAULT_QUERY_SELECTOR
} = {}) => {
  const {domain} = useContext(Context)
  const {track} = useAnalyticsTrack()
  let {current: adsRef} = useRef([])

  const config = domain.get('config')
  const {TRACKING_EVENT_NAMES} = config.get('tracking')
  const isMobile = config.get('isMobile')

  const observerOptions = isMobile ? OBSERVER_OPTIONS.MOBILE : OBSERVER_OPTIONS.DESKTOP

  const minimumTimeToBeVisible = isMobile ? MINIMUM_TIME_TO_BE_VISIBLE.MOBILE : MINIMUM_TIME_TO_BE_VISIBLE.DESKTOP

  const handleVisibleAds = useCallback(() => {
    const adsToBeTracked = adsRef.filter(
      ({isTracked, duration, startTime}) =>
        !isTracked && (duration >= minimumTimeToBeVisible || (Boolean(startTime) && !duration))
    )

    const hasAdsToBeTracked = Boolean(adsToBeTracked.length)

    if (hasAdsToBeTracked) {
      const adsToTrack = adsToBeTracked.map(({position}) => ({
        ...ads[position],
        list_position: Number(position) + 1,
        list_page_num: currentPage
      }))

      handleTrack({ads: adsToTrack})
      updateTrackedAds({ads: adsToBeTracked})
    }
  }, [ads])

  const updateTrackedAds = useCallback(
    ({ads}) => ads.forEach(({position}) => (adsRef[position] = {...adsRef[position], isTracked: true})),
    [ads]
  )

  const handleTrack = useCallback(
    async ({ads}) => {
      const trackingData = await domain.get('get_event_tracking_data_use_case').execute({
        ads,
        context: pageName,
        event: TRACKING_EVENT_NAMES.AD_IMPRESSIONS_COLLECTED_EVENT,
        filtersString: extendedTrackingData.filters,
        listOrder: extendedTrackingData.order
      })
      track(trackingData)
    },
    [ads, extendedTrackingData]
  )

  const handleBeforeUnload = () => {
    handleVisibleAds()
  }

  useEffect(() => {
    reset()
  }, [ads])

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      handleVisibleAds()
    }
  }, [ads])

  useEffect(() => {
    const observer = new window.IntersectionObserver(entries => {
      entries.forEach(({isIntersecting, target, time}) => {
        const position = target.getAttribute(POSITION_DATA_ATTRIBUTE)

        const previousStartTime = adsRef[position]?.startTime || DEFAULT_EMPTY_TIME

        const isShowedInViewport = Boolean(previousStartTime)
        const isTracked = Boolean(adsRef[position]?.isTracked)
        const roundedTime = Math.round(time)
        const roundedDuration = Math.round(time - previousStartTime)

        const startTime = (isIntersecting && !isShowedInViewport && roundedTime) || DEFAULT_EMPTY_TIME

        const duration = (!isIntersecting && isShowedInViewport && roundedDuration) || DEFAULT_EMPTY_TIME

        adsRef[position] = {
          duration,
          isTracked,
          position,
          startTime
        }
      })
    })

    const ads = document.querySelectorAll(querySelector)
    const adsArray = Array.from(ads)
    adsArray.forEach(element => observer.observe(element))

    return () => {
      observer.disconnect()
    }
  }, [ads, observerOptions, querySelector])

  const generateAttribute = useCallback(
    ({position}) => ({
      [POSITION_DATA_ATTRIBUTE]: position
    }),
    [ads]
  )

  const reset = useCallback(() => (adsRef = []), [ads])

  return {...[generateAttribute, reset], generateAttribute, reset}
}

export {useViewedAds}
